import moment from "moment";
import { getSalesByLevel3, getSalesCharts, getSalesComparison } from "@/api/financialReport";
import { consoleHandlerError, extractErrorMessage } from "@/utils/message";
import { resolveDateRange, buildHeadersByPeriod, formatPeriodLabel } from "./utils/dateRange";

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

// Separador para claves compuestas (cuenta L3 + cliente) en `accountsData`
// del shape jerárquico. Convención compartida con el use case
// `SalesReportUseCase` del backend — cambiar acá obliga a cambiar
// `HIERARCHICAL_KEY_SEPARATOR` en sales.py también.
const HIERARCHICAL_KEY_SEPARATOR = " :: ";

const state = () => ({
    /** Filtro de agrupación (Cuentas, Clientes, Tiendas, etc.) */
    activeSubFilter: "cuentas",
    /**
     * Cabeceras de columna calculadas tras el fetch.
     * Ejemplo: ["Ene 2025", "Feb 2025", ...]
     * @type {string[]}
     */
    monthHeaders: [],
    /**
     * Dict indexado por la clave de cada fila. En modo plano (cuentas/
     * categorías) la clave es el nombre de la cuenta level_3 (string simple).
     * En modo jerárquico (clientes), conviven dos tipos de claves:
     *  - Plana `<cuenta>`            → fila L2 agregada (suma de hijos).
     *  - Compuesta `<cuenta> :: <cliente>` → fila L3 individual.
     * Cada valor es: { [periodoLabel]: { value: number, prevYear: number|null } }
     * donde `prevYear` es el monto del mismo bucket del año anterior — soporte
     * de YoY a nivel celda en la tabla. `null` cuando no hay peer (cuenta nueva
     * o sin movimiento hace 12m).
     * @type {Object.<string, Object.<string, { value: number, prevYear: number|null }>>}
     */
    accountsData: {},
    /** Lista ordenada de cuentas level_3 (modo plano). Vacío si es jerárquico. */
    accounts: [],
    /** Cuentas L3 padre (modo jerárquico clientes). Vacío en modo plano. */
    level2Accounts: [],
    /**
     * { cuenta L3 → [cliente1, cliente2, ...] } en modo jerárquico. Los hijos
     * vienen ya ordenados por monto descendente del backend. `{}` en modo plano.
     */
    hierarchy: {},
    /** Rango real devuelto por el backend */
    periodRange: { init_date: null, end_date: null },
    isLoading: false,
    error: null,
    /**
     * Series para los dos gráficos del header (stacked bar + actividad).
     * El backend devuelve cada serie como `[{ period: ISO, total_amount }]`;
     * acá las normalizamos a `[{ label, value }]` donde `label` es el
     * monthHeader formateado en español (coherente con el resto del store)
     * para que el componente de chart no tenga que repetir el parseo de
     * `formatPeriodLabel`. `monthHeaders` se replica acá adentro del shape
     * porque el chart vive en un fetch separado del de la tabla — si los
     * watchers de ambos no se completaron, los headers podrían quedar
     * desalineados.
     * @type {{
     *   monthHeaders: string[],
     *   byClient: { name: string, values: number[] }[],
     *   clientCounts: number[],
     *   avgTickets: number[],
     *   hasOtros: boolean,
     *   topN: number,
     * } | null}
     */
    chartsData: null,
    isChartsLoading: false,
    chartsError: null,
    /**
     * Gráfico de comparación año-contra-año (toggle del botón "Comparación"
     * de la tabla). Vive separado de `chartsData` porque se fetchea lazy
     * (sólo al abrir el panel) y con un contrato distinto: una serie por año
     * calendario alineada a los 12 meses Ene..Dic.
     * @type {{
     *   months: number[],
     *   series: { year: number, values: (number|null)[] }[],
     *   availableYears: number[],
     * } | null}
     */
    comparisonData: null,
    isComparisonLoading: false,
    comparisonError: null,
    /**
     * Años seleccionados para comparar. Vacío hasta la primera apertura;
     * `fetchSalesComparison` lo inicializa a [añoActual, añoActual-1] a partir
     * del `endMonth` del header.
     * @type {number[]}
     */
    comparisonYears: [],
    /**
     * Subcategoría seleccionada en modo categoría (tiendas/marcas). `null` en
     * modo cuentas (no hay subcategoría) o antes de elegir una.
     * @type {string|null}
     */
    comparisonItem: null,
});

// ---------------------------------------------------------------------------
// Getters
// ---------------------------------------------------------------------------

const getters = {
    /**
     * True cuando la respuesta del backend trajo shape jerárquico (clientes
     * anidados bajo cuenta). Cambia el contrato de tableRows: en lugar de
     * filas planas vienen filas con `level: 2` (cuenta) intercaladas con sus
     * `level: 3` (clientes). El consumer filtra L3 por `expandedL2Rows`.
     */
    isHierarchical: (state) => state.level2Accounts.length > 0,

    /**
     * Construye las filas de la tabla a partir del shape recibido.
     *
     * Modo plano:    una fila por cuenta level_3.
     * Modo jerárquico: una fila L2 por cuenta + una L3 por cliente bajo esa
     *                  cuenta (en el orden de `hierarchy[cuenta]`). Cada L3
     *                  trae `parent` para filtrar visibilidad en el view.
     *
     * Cada fila lleva `values` (montos por bucket) y `prevYearValues`
     * (peer del año anterior por bucket; `null` cuando no hay peer). El
     * frontend usa ambos arrays paralelos para computar YoY a nivel celda
     * en el tooltip de cada `<td>`.
     * @returns {Array<{
     *   label: string,
     *   level?: number,
     *   parent?: string,
     *   values: number[],
     *   prevYearValues: (number|null)[],
     *   isTotal: boolean,
     * }>}
     */
    tableRows: (state, getters) => {
        const headers = state.monthHeaders;
        if (!getters.isHierarchical) {
            return state.accounts.map((account) => ({
                label: account,
                isTotal: false,
                values: headers.map(
                    (h) => state.accountsData[account]?.[h]?.value ?? 0
                ),
                prevYearValues: headers.map(
                    (h) => state.accountsData[account]?.[h]?.prevYear ?? null
                ),
            }));
        }

        const rows = [];
        state.level2Accounts.forEach((parent) => {
            rows.push({
                label: parent,
                level: 2,
                isTotal: false,
                values: headers.map(
                    (h) => state.accountsData[parent]?.[h]?.value ?? 0
                ),
                prevYearValues: headers.map(
                    (h) => state.accountsData[parent]?.[h]?.prevYear ?? null
                ),
            });

            const children = state.hierarchy[parent] || [];
            children.forEach((client) => {
                const compositeKey = `${parent}${HIERARCHICAL_KEY_SEPARATOR}${client}`;
                rows.push({
                    label: client,
                    level: 3,
                    parent,
                    isTotal: false,
                    values: headers.map(
                        (h) => state.accountsData[compositeKey]?.[h]?.value ?? 0
                    ),
                    prevYearValues: headers.map(
                        (h) => state.accountsData[compositeKey]?.[h]?.prevYear ?? null
                    ),
                });
            });
        });
        return rows;
    },

    /**
     * Fila de totales: suma de todas las cuentas (L2 en jerárquico, plano
     * en modo cuentas / categorías) por bucket. El peer del año anterior se
     * deriva de la misma forma: suma de prev_year por bucket sobre las
     * mismas keys sumables. Cuando todas las keys tienen `null` en un
     * bucket dado, el peer del total queda `null` (no había nada hace 12m).
     * @returns {{
     *   label: string,
     *   values: number[],
     *   prevYearValues: (number|null)[],
     *   isTotal: boolean,
     * }}
     */
    totalRow: (state, getters) => {
        const summableKeys = getters.isHierarchical
            ? state.level2Accounts
            : state.accounts;
        const headers = state.monthHeaders;
        const values = headers.map((h) =>
            summableKeys.reduce(
                (sum, key) => sum + (state.accountsData[key]?.[h]?.value ?? 0),
                0,
            )
        );
        const prevYearValues = headers.map((h) => {
            let sum = 0;
            let anyPeer = false;
            for (const key of summableKeys) {
                const peer = state.accountsData[key]?.[h]?.prevYear;
                if (peer != null) {
                    sum += peer;
                    anyPeer = true;
                }
            }
            return anyPeer ? sum : null;
        });
        return {
            label: "Ventas Netas",
            values,
            prevYearValues,
            isTotal: true,
        };
    },
};

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

const mutations = {
    SET_ACTIVE_SUB_FILTER(state, filter) {
        state.activeSubFilter = filter;
    },
    SET_SALES_DATA(state, {
        accounts,
        level2Accounts,
        hierarchy,
        accountsData,
        monthHeaders,
        periodRange,
    }) {
        state.accounts = accounts;
        state.level2Accounts = level2Accounts;
        state.hierarchy = hierarchy;
        state.accountsData = accountsData;
        state.monthHeaders = monthHeaders;
        state.periodRange = periodRange;
    },
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_CHARTS_DATA(state, payload) {
        state.chartsData = payload;
    },
    SET_CHARTS_LOADING(state, isLoading) {
        state.isChartsLoading = isLoading;
    },
    SET_CHARTS_ERROR(state, error) {
        state.chartsError = error;
    },
    SET_COMPARISON_DATA(state, payload) {
        state.comparisonData = payload;
    },
    SET_COMPARISON_LOADING(state, isLoading) {
        state.isComparisonLoading = isLoading;
    },
    SET_COMPARISON_ERROR(state, error) {
        state.comparisonError = error;
    },
    SET_COMPARISON_YEARS(state, years) {
        state.comparisonYears = years;
    },
    SET_COMPARISON_ITEM(state, item) {
        state.comparisonItem = item;
    },
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

const actions = {
    // Bootstrap del tab Ventas — el view sólo llama esta action en `mounted`
    // y la action orquesta las dos lecturas (tabla + gráficos) en paralelo.
    // Cada una tiene su propia signature de refetch en watchers, así que se
    // mantienen exportadas individualmente para el watch-driven refresh.
    async initVentas({ dispatch }, payload = {}) {
        await Promise.all([
            dispatch("fetchSalesData"),
            dispatch("fetchSalesCharts", payload),
        ]);
    },

    async fetchSalesData({ state, commit, rootState }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const endMonth = rootState.reportsFilters.endMonth;
            const filterPeriod = rootState.financialReports.filterPeriod;

            const { initDate, endDate } = resolveDateRange(
                endMonth,
                rootState.reportsFilters.selectedPeriodRange,
                filterPeriod,
                rootState.reportsFilters.customRange,
            );

            // Format groupBy parameter (e.g. 'cuentas', 'clientes', or category name)
            const groupBy = state.activeSubFilter.toLowerCase();

            const response = await getSalesByLevel3(initDate, endDate, groupBy, filterPeriod);
            const {
                hierarchical,
                accounts,
                level2_accounts: level2AccountsRaw,
                hierarchy: hierarchyRaw,
                report_data: reportData,
                period_range: periodRange,
            } = response.data;

            // Cabeceras según granularidad (mes/trimestre/año).
            const monthHeaders = buildHeadersByPeriod(
                periodRange.init_date,
                periodRange.end_date,
                filterPeriod
            );

            // El shape jerárquico (modo clientes) trae `level2_accounts` +
            // `hierarchy` y NO `accounts`. Las claves de `report_data` mezclan
            // L2 planas (`cuenta`) con L3 compuestas (`cuenta :: cliente`). El
            // parseo de períodos es el mismo para ambos shapes; solo cambia
            // qué llenamos en state.
            const isHierarchical = hierarchical === true;
            const dataKeys = isHierarchical
                ? Object.keys(reportData)
                : accounts;

            // Por bucket guardamos `{ value, prevYear }`. `prevYear` puede ser
            // `null` (sin peer hace 12m) — distinto de 0 (peer real que neteó
            // a cero). El frontend usa la diferencia: null pinta "—" en el
            // tooltip de YoY, 0 pinta "0,0%".
            const accountsData = {};
            dataKeys.forEach((key) => {
                const row = {};
                (reportData[key] ?? []).forEach((item) => {
                    const label = formatPeriodLabel(item.period, filterPeriod);
                    row[label] = Object.freeze({
                        value: Number(item.total_amount),
                        prevYear: item.prev_year_amount == null
                            ? null
                            : Number(item.prev_year_amount),
                    });
                });
                // Freeze por fila: Vue 2 omite el observer recursivo en objetos congelados.
                accountsData[key] = Object.freeze(row);
            });

            commit("SET_SALES_DATA", {
                accounts: Object.freeze(isHierarchical ? [] : accounts),
                level2Accounts: Object.freeze(
                    isHierarchical ? level2AccountsRaw : []
                ),
                hierarchy: Object.freeze(isHierarchical ? hierarchyRaw : {}),
                accountsData: Object.freeze(accountsData),
                monthHeaders: Object.freeze(monthHeaders),
                periodRange: Object.freeze(periodRange),
            });
        } catch (error) {
            consoleHandlerError({ error, prefix: "[salesReport]", silent: true });
            commit("SET_ERROR", extractErrorMessage(error, "Error al cargar ventas."));
        } finally {
            commit("SET_LOADING", false);
        }
    },

    async fetchSalesCharts({ commit, rootState }, payload = {}) {
        commit("SET_CHARTS_LOADING", true);
        commit("SET_CHARTS_ERROR", null);

        try {
            const endMonth = rootState.reportsFilters.endMonth;
            const filterPeriod = rootState.financialReports.filterPeriod;
            const { initDate, endDate } = resolveDateRange(
                endMonth,
                rootState.reportsFilters.selectedPeriodRange,
                filterPeriod,
                rootState.reportsFilters.customRange,
            );

            const response = await getSalesCharts(
                initDate,
                endDate,
                filterPeriod,
                payload.topN,
            );
            const {
                by_client: byClientRaw,
                client_counts: clientCountsRaw,
                avg_tickets: avgTicketsRaw,
                has_otros: hasOtros,
                top_n: topN,
                period_range: chartsPeriodRange,
            } = response.data;

            // El backend devuelve cada bucket como `{period: ISO, total_amount}`.
            // Acá derivamos:
            //   - monthHeaders: labels formateados en español (los charts los
            //     consumen como X-axis labels para que coincidan visualmente
            //     con los de la tabla).
            //   - byClient[i].values: array plano alineado a monthHeaders.
            //     Esto saca toda la lógica de zipping del componente, que sólo
            //     recibe arrays paralelos.
            // Tomamos los headers del primer serie disponible — si by_client
            // está vacío, caemos a client_counts o a un array vacío.
            const sourcePeriods = (byClientRaw[0]?.values
                || clientCountsRaw
                || []).map((entry) => entry.period);
            const monthHeaders = sourcePeriods.map(
                (iso) => formatPeriodLabel(iso, filterPeriod)
            );

            const byClient = byClientRaw.map((series) => ({
                name: series.name,
                values: series.values.map((entry) => Number(entry.total_amount || 0)),
            }));
            const clientCounts = clientCountsRaw.map(
                (entry) => Number(entry.count || 0)
            );
            const avgTickets = avgTicketsRaw.map(
                (entry) => Number(entry.amount || 0)
            );

            commit("SET_CHARTS_DATA", Object.freeze({
                monthHeaders: Object.freeze(monthHeaders),
                byClient: Object.freeze(byClient.map(Object.freeze)),
                clientCounts: Object.freeze(clientCounts),
                avgTickets: Object.freeze(avgTickets),
                hasOtros: Boolean(hasOtros),
                topN: Number(topN || 0),
                periodRange: Object.freeze(chartsPeriodRange || {}),
            }));
        } catch (error) {
            consoleHandlerError({ error, prefix: "[salesReport]", silent: true });
            commit(
                "SET_CHARTS_ERROR",
                extractErrorMessage(error, "Error al cargar gráficos de ventas.")
            );
        } finally {
            commit("SET_CHARTS_LOADING", false);
        }
    },

    // Comparación año-contra-año (lazy: el view la dispara al abrir el panel y
    // en cada cambio de mes/subcategoría/años mientras está abierto). Deriva
    // group_by/item del sub-filtro activo: categoría → filtra a la subcategoría
    // elegida; cuentas → sin filtro (total). El año en curso lo recorta el
    // backend al mes del header.
    async fetchSalesComparison({ state, commit, rootState }) {
        commit("SET_COMPARISON_LOADING", true);
        commit("SET_COMPARISON_ERROR", null);

        try {
            const endMonth = rootState.reportsFilters.endMonth;
            const endYear = moment(endMonth).year();

            // Default a [añoActual, añoActual-1] la primera vez que se abre.
            let years = state.comparisonYears;
            if (!years || years.length === 0) {
                years = [endYear, endYear - 1];
                commit("SET_COMPARISON_YEARS", years);
            }

            // Sólo el modo categoría filtra; cuentas/clientes van sin filtro.
            const sub = state.activeSubFilter;
            const isCategory = sub !== "cuentas" && sub !== "clientes";
            const groupBy = isCategory ? sub : null;
            const item = isCategory ? state.comparisonItem : null;

            const endDate = moment(endMonth).endOf("month").format("YYYY-MM-DD");
            const response = await getSalesComparison(endDate, years, groupBy, item);
            const {
                months,
                series,
                available_years: availableYears,
            } = response.data;

            commit("SET_COMPARISON_DATA", Object.freeze({
                months: Object.freeze(months || []),
                series: Object.freeze((series || []).map(Object.freeze)),
                availableYears: Object.freeze(availableYears || []),
            }));
        } catch (error) {
            consoleHandlerError({ error, prefix: "[salesReport]", silent: true });
            commit(
                "SET_COMPARISON_ERROR",
                extractErrorMessage(error, "Error al cargar la comparación.")
            );
        } finally {
            commit("SET_COMPARISON_LOADING", false);
        }
    },
};

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
